import { DirectSaleFinancialInstallmentList } from '@/components/Movements/DirectSale/DirectSaleFinancial/DirectSaleFinancialTabs';
import { PageRequest } from '@/core/request/PageRequest';
import { PageResult } from '@/core/request/PageResult';
import { Moment } from 'moment';
import { InstallmentTag } from '../InstallmentTags/InstallmentTags.type';
import { CustomerCategory } from '../../people/CustomerCategory/CustomerCategory.types';
import { TransactionPaymentType } from '../../payment-gateway/transactions/transactions.types';

export enum AccountReceivableDebtor {
  Insurer = 'domain.enums.debtor_type.insurer',
  Costumer = 'domain.enums.debtor_type.customer',
}

export enum AccountReceivableSituation {
  open = 'domain.enums.situation_installment_receivable.open',
  canceled = 'domain.enums.situation_installment_receivable.canceled',
  settled = 'domain.enums.situation_installment_receivable.settled',
  grouped = 'domain.enums.situation_installment_receivable.grouped',
}

export const AccountReceivableSituationColor = {
  [AccountReceivableSituation.open]: 'blue',
  [AccountReceivableSituation.settled]: 'green',
  [AccountReceivableSituation.grouped]: 'yellow',
  [AccountReceivableSituation.canceled]: 'red',
};

export const AccountRecivableSituationType = {
  [AccountReceivableSituation.open]: 'Aberta',
  [AccountReceivableSituation.canceled]: 'Cancelado',
  [AccountReceivableSituation.settled]: 'Liquidado',
  [AccountReceivableSituation.grouped]: 'Agrupado',
};

export interface AccountReceivable {
  id: number;
  code: string;
  companyName: string;
  debtor: AccountReceivableDebtor;
  description: string;
  bankAccountId: number | null;
  documentTypeId: number;
  documentType: string;
  dueDate: string;
  authorizationNumber: string;
  situation: AccountReceivableSituation;
  installmentValue: number;
  discountIncrease: number;
  rateValue: number;
  accountReceivableId: number;
  customerId?: number;
  insurerId?: number;
  isStoneOrder?: boolean;
  stoneDeviceId?: number;
  hasInstallmentReceivableBill: boolean;
  installmentTag?: InstallmentTag;
  conciliateInstallmentResponse?: ConciliateInstallmentResponse;
}

export type AccountReceivableListResponse = PageResult<AccountReceivable>;

export enum AccountReceivableOrderColumn {
  code = 'domain.enums.order_columns_account_receivable.code',
  company = 'domain.enums.order_columns_account_receivable.company',
  debtor = 'domain.enums.order_columns_account_receivable.debtor',
  description = 'domain.enums.order_columns_account_receivable.description',
  dueDate = 'domain.enums.order_columns_account_receivable.due_date',
  payDate = 'domain.enums.order_columns_account_receivable.pay_date',
  emissionDate = 'domain.enums.order_columns_account_receivable.emission_date',
  value = 'domain.enums.order_columns_account_receivable.value',
  documentType = 'domain.enums.order_columns_account_receivable.documentType',
  situation = 'domain.enums.order_columns_account_receivable.situation',
  documentNumber = 'domain.enums.order_columns_account_payable.documentNumber',
  installmentTag = 'domain.enums.order_columns_account_payable.installmentTag',
  authorizationNumber = 'domain.enums.financial_field_options.authorization_number',
}

export enum AccountReceivableFieldOption {
  code = 'domain.enums.order_columns_account_receivable.code',
  company = 'domain.enums.order_columns_account_receivable.company',
  debtor = 'domain.enums.order_columns_account_receivable.debtor',
  description = 'domain.enums.order_columns_account_receivable.description',
  dueDate = 'domain.enums.order_columns_account_receivable.due_date',
  value = 'domain.enums.order_columns_account_receivable.value',
  documentType = 'domain.enums.order_columns_account_receivable.documentType',
}

export const AccountReceivableColumnConfig: Record<
  string,
  AccountReceivableOrderColumn
> = {
  [AccountReceivableFieldOption.code]: AccountReceivableOrderColumn.code,
  [AccountReceivableFieldOption.company]: AccountReceivableOrderColumn.company,
  [AccountReceivableFieldOption.debtor]: AccountReceivableOrderColumn.debtor,
  [AccountReceivableFieldOption.description]:
    AccountReceivableOrderColumn.description,
  [AccountReceivableFieldOption.dueDate]: AccountReceivableOrderColumn.dueDate,
  [AccountReceivableFieldOption.value]: AccountReceivableOrderColumn.value,
  [AccountReceivableFieldOption.documentType]:
    AccountReceivableOrderColumn.documentType,
};

export type AccountReceivableListRequest = PageRequest & {
  search?: string;
  code?: string;
  companyId?: number;
  customerId?: number;
  insurerId?: number;
  debtor?: string;
  description?: string;
  documentTypeId?: number;
  dueDate?: string;
  emissionDate?: string;
  payDate?: string;
  situation?: AccountReceivableSituation;
  installmentValue?: number;
  orderColumns?: AccountReceivableOrderColumn;
  accidentNumber?: string;
  bankAccountId?: number;
  costCenterId?: number;
  documentNumber?: string;
  licensePlate?: string;
  workOrderNumber?: string;
  initialDueDate?: string;
  finalDueDate?: string;
  initialPayDate?: string;
  finalPayDate?: string;
  initialEmissionDate?: string;
  finalEmissionDate?: string;
  observation?: string;
  authorizationNumber?: string;
};

export type AccountReceivableFormValues = Omit<
  AccountReceivableCreate,
  'emissionDate' | 'dueDate' | 'payDay'
> & {
  emissionDate: Moment;
  dueDate?: Moment;
  payDay?: Moment;
  netValue?: number;
  amountReceivable?: number;
  customerName?: string;
  insurerName?: string;
  installmentId: number;
  updateTariff: boolean;
  amountAdjusted?: number;
  authorizationNumber?: string;
};

export type AccountReceivableCreate = {
  companyId: number;
  costCenterId: number;
  planAccountId: number;
  insuerId?: number;
  customerId?: number;
  debtor: AccountReceivableDebtor;
  installmentNumber: number;
  bankAccountId: number;
  description: string;
  documentNumber: string;
  documentTypeId: number;
  emissionDate?: string;
  dueDate?: string;
  payDay?: string;
  rateValue?: number;
  discountIncrease: number;
  installmentValue: number;
  observation: string;
};

export interface AccountReceivableCreateResponse {
  id: number;
  code: number;
  companyId: number;
  costCenterId: number;
  planAccountId: number;
  insurerId: number;
  customerId: number;
  installmentReceivables: AccountReceivable;
}

export interface AccountReceivableEdit {
  accountReceivableId: number;
  installmentId: number;
  insuerId?: number;
  customerId?: number;
  planAccountId: number;
  costCenterId: number;
  description: string;
  documentNumber: string;
  documentTypeId: number;
  emissionDate?: string;
  dueDate?: string;
  payDay?: string;
  bankAccountId: number;
  discountIncrease: number;
  installmentValue: number;
  debtor: AccountReceivableDebtor;
  observation: string;
  currentSituation: AccountReceivableSituation;
}

export interface AccountReceivableInstallment {
  id: number;
  installmentNumber: number;
  accountReceivableId: number;
  situation: AccountReceivableSituation;
  bankAccountId: number;
  conciliationDate: string;
  creationDate: string;
  description: string;
  documentNumber: string;
  documentTypeId: number;
  emissionDate: string;
  dueDate: string;
  rateValue: number;
  discountIncrease: number;
  payDate: string;
  updateDate: string;
  installmentValue: number;
  observation: string;
  hasInstallmentReceivableBill: boolean;
  billId?: number;
  billOurNumber?: string;
  billPrintProtocol?: string;
  conciliateInstallmentResponse?: ConciliateInstallmentResponse;
  installmentTag?: InstallmentTag;
  amountAdjusted?: number;
  authorizationNumber?: string;
}

export interface AccountReceivableDetail {
  id: number;
  code: number;
  companyId: number;
  costCenterId: number;
  insurerId: number;
  customerId: number;
  customerCategory?: CustomerCategory;
  planAccountId: number;
  debtor: AccountReceivableDebtor;
  installmentReceivables: AccountReceivableInstallment[];
  isStoneOrder: boolean;
  workOrderId?: number;
  directSaleId?: number;
  transaction?: {
    createdAt: string;
    id: number;
    paymentType: TransactionPaymentType;
    terminalNsu: string;
    totalAmount: number;
  };
  authorizationNumber?: string;
}

export interface ConciliateInstallmentResponse {
  transactionName: string;
  transactionValue: number;
  transactionPayDate: string;
  conciliatedDate: string;
}

export interface AccountRecivableSettleSingleInstallment {
  id: number;
  installmentReceivableId: number;
  bankAccountId: number;
  documentTypeId: number;
  payDate: string;
  discountIncrease: number;
  netValue: number;
  currentSituation?: AccountReceivableSituation;
}

export interface AccountReceivableInstallmentCancel {
  id: number;
  installmentId: number;
}

export interface AccountRecivablesettlePartiallyInstallment
  extends AccountRecivableSettleSingleInstallment {
  dueDate: string;
  amountToBeSettled: number;
}

export interface AccountReceivableSettleMultipleInstallmentRequest {
  installmentReceivableId: number[];
  accountReceivableIds: number[];
  bankAccountId: number;
  payDate: string;
  discountIncrease?: number;
}

export interface AccountReceivableReverseInstallmentRequest {
  id?: number;
  installmentReceivableId?: number;
  planAccountId: number;
  reversalDate: string;
}

export interface AccountRecivableSplitInstallmentRequest {
  installmentReceiveId: number;
  accountReceiveId: number;
  valueTotal: number;
  fromThatDate: string;
  intervalDays: number;
  quantityInstallments: number;
  currentSituation: AccountReceivableSituation;
}
export interface AccountRecivableSplitInstallmentFormValues
  extends Omit<AccountRecivableSplitInstallmentRequest, 'fromThatDate'> {
  fromThatDate: Moment;
}

export interface AccountRecivableRenegotiateInstallmentRequest {
  accountId: number;
  installmentReceiveId: number;
  valueTotal: number;
  discountIncrease: number;
  documentTypeId: number;
  fromThatDate: string;
  quantityInstallments: number;
  intervalDays: number;
  currentSituation: AccountReceivableSituation;
}

export interface AccountRecivableRenegotiateInstallmentResponse {
  accountId: number;
  installmentId: number;
}

export interface AccountRecivableRenegotiateInstallmentFormValues
  extends Omit<AccountRecivableRenegotiateInstallmentRequest, 'fromThatDate'> {
  fromThatDate: Moment;
  valueTotalInstallments: number;
}

export interface ListAccountReceivableForGroupingRequest extends PageRequest {
  companyId?: number;
  debtor?: AccountReceivableDebtor;
  insurerId?: number;
  customerId?: number;
}

export type ListAccountReceivableForGroupingResponse = {
  accountId: number;
  code: number;
  description: string;
  installmentNumber: number;
  totalValue: number;
};

export interface AccountReceivableGroupingInstallmentFormValues {
  costCenterId: number;
  totalValue: number;
  planAccountId: number;
  installmentsFirstDueDate: Moment;
  quantityInstallments: number;
  intervalDays: number;
  accountIds: number[];
}

export interface AccountReceivableInstallmentGroupingItem {
  description: string;
  documentNumber?: string;
  documentTypeId?: number;
  bankAccountId?: number;
  dueDate: Moment;
  installmentValue: number;
}

export interface AccountReceivableGenerateGroupingRequest {
  costCenterId: number;
  planAccountId: number;
  installmentsFirstDueDate: string;
  quantityInstallments: number;
  intervalDays: number;
  totalValue: number;
  accountIds: number[];
  installmentFromGroupings: (Omit<
    AccountReceivableInstallmentGroupingItem,
    'dueDate'
  > & { dueDate: string })[];
}

export interface ListAccountReceivableBySaleRequest extends PageRequest {
  saleId?: number;
}

export interface AccountReceivableBySaleRequest {
  saleId: number;
  companyId: number;
  planAccountId: number;
  customerId: number;
  costCenterId?: number;
  authorizationNumber: string;
  bankAccountId: number;
  installmentReceivableSales: (Omit<
    DirectSaleFinancialInstallmentList,
    'dueDate'
  > & {
    dueDate: string;
  })[];
}

export interface AccountReceivableFinancialBySaleRequest {
  saleId?: number;
}

export interface AccountReceivableFinancialBySaleResponse {
  totalValuePending: number;
  totalValueGenerated: number;
  totalAmount: number;
}

export interface AccountReceivableFinancialPending {
  totalValuePendent: number | null;
  totalPrivateValue: number | null;
  totalInsurerValue: number | null;
  issDiscount: number | null;
  issDiscountPercentage: number | null;
}

export enum FinancialSummaryInstallmentType {
  Insurer = 'domain.enums.installment_type.insurer',
  Customer = 'domain.enums.installment_type.customer',
}

export interface FinancialSummaryInstallment {
  code: number;
  dueDate: string;
  creationDate: string;
  value: number;
  situation: string;
  type: FinancialSummaryInstallmentType;
  bankingEntryId?: number;
  accountReceivableId: number;
  installmentReceivableId: number;
  isStoneOrder: boolean;
  stoneDeviceId: number;
}

export interface AccountReceivableFinancialSummary {
  totalWorkOrderValue: number;
  totalPrivateValue: number;
  totalProcessedPrivateValue: number;
  totalUnprocessedPrivateValue: number;
  totalInsurerValue: number;
  totalProcessedInsurerValue: number;
  totalUnprocessedInsurerValue: number;
  installmentReceivableCustomer: FinancialSummaryInstallment[];
  installmentReceivableInsurer: FinancialSummaryInstallment[];
}

export interface GenerateFinancialInstallment {
  documentTypeId: number;
  dueDate: string;
  discountIncrease: number;
  installmentNumber: number;
  installmentValue: number;
}

export interface AccountReceivableGenerateFinancial {
  workOrderId: number;
  companyId: number;
  costCenterId?: number;
  planAccountId: number;
  insuerId?: number;
  customerId?: number;
  authorizationNumber?: string;
  installmentReceivableWorkOrders: GenerateFinancialInstallment[];
  bankAccountId?: number;
}

export interface AccountReceivableRangeDateResponse {
  id: number;
  installmentNumber: number;
  accountReceivableId: number;
  accountReceivableCode: number;
  accountReceivableCustomerId: number;
  accountReceivableCustomerName: string;
  accountReceivableInsurerId: number;
  accountReceivableInsurerName: string;
  situation: AccountReceivableSituation;
  bankAccountId: number;
  conciliationDate: string;
  creationDate: string;
  description: string;
  discountIncrease?: number;
  documentNumber: string;
  documentTypeId: number;
  emissionDate: string;
  dueDate: string;
  payDate: string;
  updateDate: string;
  installmentValue: number;
  installmentRateValue: number;
  observation: string;
}

export interface AccountReceivableReverseMultipleInstallmentsRequest {
  installmentReceivableId: React.Key[];
  planAccountId: number;
  reversalDate: string;
}

export interface AccountReceivableDeleteMultipleInstallmentsRequest {
  installmentIds: React.Key[];
}

export interface AccountReceivableListGroupingResponse {
  id: number;
  code: string;
  companyName: string;
  debtor: string;
  description: string;
  documentTypeId: number;
  documentType?: string;
  accountReceivablePlanAccountId: number;
  planAccount?: string;
  dueDate: string;
  situation: string;
  installmentValue: number;
  bankAccountId?: number;
  discountIncrease: number;
  rateValue: number;
  accountReceivableId: number;
  customerId: number;
  insurerId?: number;
  hasInstallmentReceivableBill: boolean;
}

export interface AccountReceivableArchive {
  id: number;
  fileName: string;
  filePath: string;
}

export interface AccountReceivableArchiveCreateRequest {
  accountReceivableId: number;
  files: File[];
}

export interface AccountReceivableArchiveRemoveRequest {
  accountReceivableId: number;
  fileId: number;
}

export interface AccountReceivableIncludeTagRequest {
  accountReceivableId: number;
  installmentReceivableId: number;
  installmentTagId?: number | null;
}

export interface AccountReceivableExist {
  exists: boolean;
}
